import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowUp,
  BookOpen,
  BotMessageSquare,
  ExternalLink,
  FileText,
  Loader2,
  ThumbsUp,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { NewBadge } from "@/components/NewBadge";
import { Feature, useSession } from "@/components/session-provider";
import { recordDeflectionAnswered, suggestArticles, type ArticleListItem } from "@/lib/knowledge";
import { isNew, markFeatureSeen } from "@/lib/whats-new";
import { cn } from "@/lib/utils";

const MAX_MESSAGE_CHARACTERS = 2000;

/** Mirrors the server's turn shape. */
interface Turn {
  speaker: "user" | "agent";
  text: string;
}

/** Mirrors the server's SupportAgentChatResponse. Note what is absent: nothing that decides access. */
interface SupportAgentChatResponse {
  reply: string;
  resolved: boolean;
  suggestedSubject: string | null;
  suggestedDescription: string | null;
  categoryId: string | null;
  categoryName: string | null;
  /** A URL is pulled out of the real Troubleshooting note text server-side, never something
   * the model stated or paraphrased. See `AssistantEndpoints.ExtractDocumentationLinks`. */
  documentationLinks: { label: string; url: string }[];
}

/**
 * ⚠️ Cosmetic only, the same convention {@link ArticleDeflectionPanel} and the old draft panel
 * both used for a call with no real progress events to report.
 */
const WORKING_STEPS = ["Reading that…", "Thinking it through…", "Writing a reply…"] as const;

/** What the parent does once the person is ready to hand this off to a ticket. */
export interface SupportAgentHandoff {
  subject: string;
  description: string;
  categoryId: string | null;
}

/**
 * Nova (internally "TARS" throughout the rest of this file and its system prompt — only the
 * display name changed) — the service desk's conversational front door. Named, given a role and a hard scope
 * boundary in its own system prompt (`SupportAgentAssistant.SystemPrompt`): it may only help
 * with the technology this desk actually supports, it never invents a fact about a specific ARS
 * system, and it never touches anything clinical.
 *
 * ⚠️ Everything it says is a SUGGESTION. It cannot set priority, assignee, team or sensitivity —
 * Q11.6 and threat model §6.2: no AI output crosses an authorization boundary. It cannot create a
 * ticket by itself either — {@link onEscalate} fills the wizard's own subject/description fields
 * (and, when the server matched one, the category) and the person reviews and confirms through
 * the ordinary Continue → Review → Create flow, for "Just create a ticket" because they are
 * stuck. {@link onResolved} takes the shorter path for "This solved my issue" — the caller
 * creates the ticket directly, because the contact fields it needs were already collected before
 * this chat ever opened (see `new.tsx`'s contact gate) — but either way a ticket still gets
 * created for tracking.
 *
 * ⚠️ THE ONLY THING ON THIS STEP WHILE THE CHAT IS UP. No guidance panel, no related articles,
 * no form fields — see the caller (`new.tsx`'s `showChat`). The chat is the whole page until it
 * hands off to one of those, deliberately: describing a problem once, to TARS, rather than to a
 * chat and a form at the same time.
 *
 * The model sees ONLY this conversation, the active category names, and — for the category
 * already chosen — any Troubleshooting-kind KB articles tagged to it, read as its own internal
 * instructions and never shown to the person verbatim (D-177). It is never given an existing
 * ticket, another person's text, or anything else from the database.
 *
 * ⚠️ **A REAL ARTICLE MAY ALSO SURFACE, SEPARATELY FROM ANYTHING THE MODEL SAYS.** Once TARS
 * has formed an actual read of the problem (a `suggestedSubject`/`suggestedDescription`, not raw
 * chat noise), that summary is run once — never on every turn, matching {@link
 * ArticleDeflectionPanel}'s own cost reasoning — through the SAME semantic search that panel
 * uses (`suggestArticles`, D-174, locked behind `ai.kb-deflection`). A match is shown as its own
 * card, with a real link to the real article — TARS's own prompt and output schema are entirely
 * unaware this happened. That separation is deliberate: the model never states or paraphrases
 * what the article says, so nothing it could hallucinate ever reaches the person as if it were
 * the article's own words. Confirming "This solved it" ends the conversation with **no ticket
 * created**, exactly like the panel's own "Nothing has been raised" — deflection's whole point.
 *
 * ⚠️ **WHEN NOTHING MATCHES, A DOCUMENTATION LINK FROM THE TROUBLESHOOTING NOTES MAY FALL BACK
 * IN ITS PLACE — never the article those notes came from, which stays exactly as invisible as
 * D-177 already made it.** A URL is a different kind of fact than a paraphrased sentence: it
 * either matches the source exactly or it is wrong, so it is pulled out of the real note text
 * server-side (`AssistantEndpoints.ExtractDocumentationLinks`) rather than ever asked of the
 * model. This is why the category fed back to the server tracks what TARS itself resolves,
 * not only the prop it started with: without that, a conversation that started with no
 * category (the top-bar launcher) could never reach the notes — or a link inside them — at all.
 */
export function SupportAgentChat({
  name = "Nova",
  greeting,
  categoryId,
  onResolved,
  onEscalate,
  onSkip,
  onDeflected,
  creating = false,
  showSkip = true,
}: {
  /** What the assistant currently calls itself — from `Reference.assistantName`, "Nova" until
   * an Owner or Super Admin renames it (`AiAssistantIdentity`). Defaults to "Nova" so a caller
   * that has not loaded reference data yet still gets today's behaviour. */
  name?: string;
  /** What it opens the conversation with, from `Reference.assistantGreeting` — an Owner or
   * Super Admin's own sentence when set, otherwise falls back to the built-in one below.
   * Never templated with `name`: a custom greeting is the administrator's own words, in full. */
  greeting?: string | null;
  /** The category already chosen on the wizard, if any — sent so the server can look up
   * Troubleshooting notes for it. Never trusted for anything else. */
  categoryId: string | null;
  /** "This solved my issue" — the person is satisfied, so the caller creates the ticket
   * directly for tracking rather than revealing the manual form. */
  onResolved: (handoff: SupportAgentHandoff) => void;
  /** "Just create a ticket" — the caller reveals the manual form, pre-filled, for review. */
  onEscalate: (handoff: SupportAgentHandoff) => void;
  onSkip: () => void;
  /** A suggested KB article resolved it — no ticket was created. Optional: the panel already
   * shows its own confirmation in place, so a caller only needs this for a side effect (e.g.
   * closing a dialog it opened the chat in). */
  onDeflected?: () => void;
  /** Whether the parent is currently turning a "This solved my issue" press into a ticket —
   * disables both buttons and swaps the resolved button's label so a second press cannot fire
   * a second create. */
  creating?: boolean;
  /** Whether the "Write it myself" link is offered. Default true (the new-ticket wizard, which
   * already shows the manual form beside this chat — the link just reveals it in place). The
   * top-bar launcher passes false: that entry point exists specifically to offer the assistant,
   * so the manual escape hatch is the wizard's own "New ticket" affordance instead. This only
   * hides the link — the automatic fallback below (a 404 mid-conversation) still calls `onSkip`
   * regardless, because that is a functional recovery, not the chooser. */
  showSkip?: boolean;
}) {
  const { user, hasFeature } = useSession();

  const [messages, setMessages] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);

  const [articleSuggestion, setArticleSuggestion] = useState<ArticleListItem | null>(null);
  const [articleAnswered, setArticleAnswered] = useState(false);
  const [markingAnswered, setMarkingAnswered] = useState(false);
  const [documentationLinks, setDocumentationLinks] = useState<{ label: string; url: string }[]>(
    [],
  );

  // The category TARS itself resolved, once it has — seeded from the prop (the wizard's own
  // pre-chosen category, if any) and updated once the server matches one from the
  // conversation. Without this, a categoryless conversation (the top-bar launcher) would never
  // send a category back on later turns, so Troubleshooting notes — and any documentation link
  // inside them — could never be fetched even after TARS worked out what this is.
  const [effectiveCategoryId, setEffectiveCategoryId] = useState(categoryId);

  // Same reach check ArticleDeflectionPanel uses, verbatim — this is the same feature, offered
  // from a second surface, not a second permission model.
  const canDeflect =
    hasFeature(Feature.AiKbDeflection) &&
    hasFeature(Feature.KnowledgeBase) &&
    (user?.canSeeKnowledgeBase ?? false);

  // At most once per conversation — a real Azure OpenAI call and PHI-capable text leaving the
  // process, same cost ArticleDeflectionPanel restricts to an explicit press. Firing it once,
  // off the assistant's own distilled understanding rather than every turn, keeps it automatic
  // without multiplying that cost per message.
  const deflectionTried = useRef(false);

  // The server's current best read of this conversation as a ticket, refreshed every turn.
  // Null only until the first reply lands — the handoff button falls back to the raw
  // conversation text below rather than waiting on it, so either button always does something
  // even if TARS never managed a usable reply.
  const draftRef = useRef<{
    subject: string;
    description: string;
    categoryId: string | null;
  } | null>(null);

  const logRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!busy) {
      setStep(0);
      return;
    }
    const timer = window.setInterval(() => setStep((s) => (s + 1) % WORKING_STEPS.length), 1100);
    return () => window.clearInterval(timer);
  }, [busy]);

  // Scrolls to the newest message. Not the whole page — just this panel's own log, so sending
  // a message on a long wizard step does not yank the screen out from under the person.
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Announced once, on arrival — the same D-168 mechanism every other "New" mark in this
  // product uses, applied outside the navigation for the first time.
  const [showNew] = useState(() => isNew("support-agent"));
  useEffect(() => {
    markFeatureSeen("support-agent");
  }, []);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;

    const next = [...messages, { speaker: "user" as const, text }];
    setMessages(next);
    setInput("");
    if (boxRef.current) boxRef.current.style.height = "";
    setBusy(true);
    setError(null);

    try {
      const reply = await api<SupportAgentChatResponse>("/api/assistant/chat", {
        method: "POST",
        body: { conversation: next, categoryId: effectiveCategoryId },
      });

      setMessages((m) => [...m, { speaker: "agent", text: reply.reply }]);
      setResolved(reply.resolved);
      setDocumentationLinks(reply.documentationLinks);
      if (reply.categoryId) setEffectiveCategoryId(reply.categoryId);
      draftRef.current = {
        subject: reply.suggestedSubject ?? draftRef.current?.subject ?? "",
        description: reply.suggestedDescription ?? draftRef.current?.description ?? "",
        categoryId: reply.categoryId,
      };

      // Fire-and-forget, and never blocking this reply's own render — a miss or a failure
      // here must look identical to nobody having searched at all (ArticleDeflectionPanel's
      // "the courtesy fails as one"). Off the assistant's OWN summary, not the raw messages:
      // a distilled read of the problem is a better query than whatever was just typed.
      if (
        canDeflect &&
        !deflectionTried.current &&
        reply.suggestedSubject &&
        reply.suggestedDescription
      ) {
        deflectionTried.current = true;

        void suggestArticles(`${reply.suggestedSubject}\n\n${reply.suggestedDescription}`)
          .then((found) => {
            if (found.length > 0) setArticleSuggestion(found[0]);
          })
          .catch(() => {
            // Advice that was unavailable, not a claim about anything said.
          });
      }
    } catch (e) {
      // ⚠️ A 404 here means the feature was switched off mid-conversation (rare, but the
      // endpoint fails closed exactly like this — AssistantEndpoints.cs). There is nothing
      // left to talk to, so rather than leave the person looking at a dead chat panel with
      // only the small "write it yourself" link to notice, fall back to the form for them.
      if (e instanceof ApiError && e.status === 404) {
        onSkip();
        return;
      }

      // Never blocks the conversation, and never blocks the ticket underneath it — the
      // person can keep typing, hand off with whatever the assistant last suggested, or
      // write the ticket themselves.
      setError(
        e instanceof ApiError
          ? e.message
          : `${name} could not respond just now. You can keep going, or connect with a tech directly.`,
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * "This solved it" on the suggested article — ends the conversation with NO ticket created,
   * matching ArticleDeflectionPanel's own "Nothing has been raised" exactly, because this is
   * the same outcome reached from a second surface.
   */
  async function markArticleAnswered() {
    if (!articleSuggestion || markingAnswered) return;
    setMarkingAnswered(true);

    try {
      await recordDeflectionAnswered(articleSuggestion.collectionSlug, articleSuggestion.slug);
    } catch {
      // The count is the desk's own measurement, not the person's outcome — matching
      // ArticleDeflectionPanel's identical swallow. Their answer is still their answer.
    } finally {
      setArticleAnswered(true);
      setMarkingAnswered(false);
      onDeflected?.();
    }
  }

  /**
   * The box grows with what is typed, capped rather than unbounded — unlike the ticket
   * thread's own composer (D-154), this panel sits inside a fixed wizard step alongside a
   * message log and two hand-off buttons, so letting it grow past a few lines would push
   * those out of view instead of just using the page's own scroll.
   */
  function autoGrow(el: HTMLTextAreaElement) {
    el.style.height = "";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  /** The current best read of this conversation as a ticket — the server's suggestion if one
   * has arrived, otherwise the person's own words, never a guess. */
  function currentHandoff(): SupportAgentHandoff {
    const draft = draftRef.current;

    if (draft && (draft.subject || draft.description)) {
      return draft;
    }

    const said = messages
      .filter((m) => m.speaker === "user")
      .map((m) => m.text)
      .join("\n\n");

    return {
      subject: said.slice(0, 100),
      description: said,
      categoryId: null,
    };
  }

  const started = messages.length > 0;
  const disabled = busy || creating;

  return (
    // ⚠️ THREE SECTIONS, EACH ITS OWN ROW, SEPARATED BY A HAIRLINE — the header, the
    // conversation, and the composer/hand-off — the same anatomy Claude's and ChatGPT's own
    // chat panels use, rather than five loosely-stacked blocks with a margin between each.
    // `overflow-hidden` on this element is what lets the header and the actions footer keep
    // the panel's own rounded corners without needing to round themselves.
    <section className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <header className="flex items-center gap-2.5 border-b border-border px-4 py-3">
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <BotMessageSquare className="h-5 w-5" aria-hidden />
          <span
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-success"
            aria-hidden
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h2 className="truncate text-sm font-semibold">{name}</h2>
            {showNew && <NewBadge />}
          </div>
          <p className="text-xs text-muted-foreground">Service desk support agent</p>
        </div>
        {showSkip && (
          <button
            type="button"
            onClick={() => onSkip()}
            disabled={disabled}
            className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-60"
          >
            Write it myself
          </button>
        )}
      </header>

      {/* The conversation. A dotted paper behind the messages, the same `chat-surface` the
          ticket thread uses, so the two chat surfaces in this product read as one family. */}
      <div ref={logRef} className="chat-surface min-h-64 max-h-[26rem] overflow-y-auto px-4 py-4">
        <div className="space-y-4">
          <ChatBubble speaker="agent">
            {greeting?.trim() || (
              <>
                Hi, I'm {name}. Tell me what's going on and I'll try to help sort it out — or get
                you straight to a tech if that's quicker.
              </>
            )}
          </ChatBubble>

          {messages.map((m, i) => (
            <ChatBubble key={i} speaker={m.speaker}>
              {m.text}
            </ChatBubble>
          ))}

          {busy && (
            <ChatBubble speaker="agent">
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <span className="flex items-center gap-1" role="status" aria-live="polite">
                  <span className="sr-only">{WORKING_STEPS[step]}</span>
                  <span
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]"
                    aria-hidden
                  />
                  <span
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]"
                    aria-hidden
                  />
                  <span
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-current"
                    aria-hidden
                  />
                </span>
                <span aria-hidden className="text-xs">
                  {WORKING_STEPS[step]}
                </span>
              </span>
            </ChatBubble>
          )}

          {resolved && !busy && (
            <div className="flex items-center gap-1.5 rounded-lg bg-success/10 px-3 py-2 text-xs font-medium text-success">
              <ThumbsUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Sounds like that's sorted — press "This solved my issue" below whenever you're ready.
            </div>
          )}

          {/* A real article, found by the SAME semantic search ArticleDeflectionPanel uses —
              never something TARS said or paraphrased. Silent when nothing matches well, same
              as every other KB courtesy in this product ("the courtesy fails as one"). */}
          {articleSuggestion && !articleAnswered && (
            <div className="rounded-xl border border-border bg-background p-3 shadow-sm">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <BookOpen className="h-3.5 w-3.5" aria-hidden />
                This might help
              </p>
              <Link
                to="/kb/$collection/$slug"
                params={{
                  collection: articleSuggestion.collectionSlug,
                  slug: articleSuggestion.slug,
                }}
                target="_blank"
                rel="noreferrer"
                className="group mt-1.5 flex items-start gap-2"
              >
                <ExternalLink
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium group-hover:underline">
                    {articleSuggestion.title}
                  </span>
                  {articleSuggestion.summary && (
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {articleSuggestion.summary}
                    </span>
                  )}
                </span>
              </Link>
              <button
                type="button"
                onClick={() => void markArticleAnswered()}
                disabled={markingAnswered}
                className="mt-2 inline-flex h-7 items-center rounded-lg border border-input px-2 text-xs font-medium transition hover:bg-accent disabled:opacity-50"
              >
                This solved it
              </button>
            </div>
          )}

          {articleAnswered && (
            <div className="flex items-center gap-1.5 rounded-lg bg-success/10 px-3 py-2 text-xs font-medium text-success">
              <ThumbsUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Glad that helped. Nothing has been raised.
            </div>
          )}

          {/* A FALLBACK — only when the knowledge base itself turned up nothing (no
              articleSuggestion). A URL pulled verbatim out of the real Troubleshooting note
              (ExtractDocumentationLinks), never something TARS said: the article it came from
              stays exactly as invisible as it always has (D-177), only a link it already
              contained reaches here. */}
          {!articleSuggestion && !articleAnswered && documentationLinks.length > 0 && (
            <div className="rounded-xl border border-border bg-background p-3 shadow-sm">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Related documentation
              </p>
              <ul className="mt-1.5 space-y-1">
                {documentationLinks.map((link) => (
                  <li key={link.url}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="border-t border-border px-4 py-2 text-xs text-destructive">
          {error}
        </p>
      )}

      {/* Nothing left to do once an article has already solved it — the composer and the
          hand-off buttons below both go, matching ArticleDeflectionPanel's own panel, which
          replaces itself entirely with the same confirmation rather than leaving controls
          for an action that no longer applies. */}
      {articleAnswered ? null : (
        <>
          {/* The composer — one rounded pill holding the textarea and the send button, the same
          shape Claude and ChatGPT both use, rather than a bordered box beside a separate
          square button. */}
          <div className="border-t border-border bg-card p-3">
            <div
              className={cn(
                "flex items-end gap-2 rounded-3xl border border-input bg-background py-1.5 pl-4 pr-1.5 shadow-sm transition",
                "focus-within:border-ring focus-within:ring-1 focus-within:ring-ring",
              )}
            >
              <label htmlFor="support-agent-input" className="sr-only">
                Message {name}
              </label>
              <textarea
                id="support-agent-input"
                ref={boxRef}
                value={input}
                rows={1}
                onChange={(e) => {
                  setInput(e.target.value.slice(0, MAX_MESSAGE_CHARACTERS));
                  autoGrow(e.target);
                }}
                onKeyDown={(e) => {
                  // Enter sends; Shift+Enter still inserts a newline for a longer message.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                maxLength={MAX_MESSAGE_CHARACTERS}
                disabled={disabled}
                placeholder="Message…"
                className="min-h-7 flex-1 resize-none overflow-y-auto bg-transparent py-1 text-base leading-relaxed focus:outline-none disabled:opacity-60 md:text-sm"
              />
              <button
                type="button"
                onClick={() => void send()}
                disabled={disabled || !input.trim()}
                aria-label="Send"
                className="mb-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-30 disabled:shadow-none"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <ArrowUp className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>

          {/* The two ways out of this chat — always visible together once there is something to
          act on, and deliberately loud: this is the one decision point on the whole step.
          "This solved my issue" carries the emphasis; "Just create a ticket" is the quieter
          escape hatch, not an equally-weighted second command competing for the same glance. */}
          {started && (
            <div className="border-t border-border bg-muted/30 px-3 py-3">
              <p className="mb-2 text-center text-[11px] text-muted-foreground">
                Whenever you're ready
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => onResolved(currentHandoff())}
                  disabled={disabled}
                  className={cn(
                    "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-success px-3 text-sm font-semibold text-success-foreground shadow-sm transition hover:opacity-90 disabled:opacity-60",
                    resolved && "ring-2 ring-success/50 ring-offset-2 ring-offset-background",
                  )}
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <ThumbsUp className="h-4 w-4" aria-hidden />
                  )}
                  {creating ? "Creating your ticket…" : "This solved my issue"}
                </button>

                <button
                  type="button"
                  onClick={() => onEscalate(currentHandoff())}
                  disabled={disabled}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-input bg-card px-3 text-sm font-semibold text-foreground shadow-sm transition hover:bg-accent disabled:opacity-60"
                >
                  <FileText className="h-4 w-4" aria-hidden />
                  Just create a ticket
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * ⚠️ ONLY THE PERSON'S OWN MESSAGE IS A BUBBLE. The assistant's reply is plain text beside its
 * own small avatar — the layout Claude and ChatGPT both use — rather than two rows of equally
 * heavy bubbles down the middle of the panel. There is exactly one assistant here (this is a
 * single named chat, not the multi-author ticket thread), so the avatar is the same fixed bot
 * icon every time rather than a per-author `Avatar` lookup.
 */
function ChatBubble({
  speaker,
  children,
}: {
  speaker: "user" | "agent";
  children: React.ReactNode;
}) {
  if (speaker === "user") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-sm leading-relaxed text-primary-foreground shadow-sm">
          {children}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5">
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"
        aria-hidden
      >
        <BotMessageSquare className="h-3.5 w-3.5" />
      </span>
      <p className="max-w-[85%] whitespace-pre-wrap pt-0.5 text-sm leading-relaxed text-foreground">
        {children}
      </p>
    </div>
  );
}
